// types/popForms.ts

import { isEmpty } from 'lodash'
import { POP_REFERENCE_TYPE } from 'constants/enums'

export interface BaseMeta {
  rid: string
  is_long_range_pop: boolean
  pop_type: number
  is_send_push_notification?: boolean
  users?: string[]
  groups?: string[]
  tags?: string[]
  is_scheduled?: boolean
}

export interface BaseData {
  pop_title?: string
  pop_body?: string
  ack_btn_text?: string
  push_title?: string
  push_body?: string
  repop_push_title?: string
  repop_push_body?: string
  profile_image_url?: string | null
  pop_image_url?: string | null
  video?: string | null
}

export interface RePopFields {
  is_repop_send_push_notification?: boolean
  repop_main_btn?: string
  repop_sec_btn?: string
  is_pop_post?: boolean
  repop_users?: string[]
  repop_tags?: string[]
  repop_groups?: string[]
}

export interface AiFields {
  prompt_id: string
  is_inj_reports?: boolean
  is_voice_mode?: boolean
}

export interface ActionPopFields {
  reference_type: number
  reference_params?: Record<string, any>
}

export interface DefaultPopForm extends BaseMeta {
  pop_type: PopType.Default
  data: BaseData
  users: string[]
  tags: string[]
  groups: string[]
}

export interface RePopForm extends BaseMeta {
  pop_type: PopType.RePop
  data: BaseData & RePopFields
  is_custom_avatar: boolean
}

export interface ChatGptPopForm extends BaseMeta {
  pop_type: PopType.ChatGpt
  data: Pick<BaseData, 'pop_body' | 'ack_btn_text' | 'push_title' | 'push_body'>
  users: string[]
}

export interface FakeRePopForm extends BaseMeta {
  pop_type: PopType.FakeRe
  data: BaseData & RePopFields
  users: string[]
}

export interface AiPopForm extends BaseMeta {
  pop_type: PopType.AiPop
  data: BaseData & AiFields & { prompt_id: string }
}

export interface AiRePopForm extends BaseMeta {
  pop_type: PopType.AiRePop
  data: BaseData & RePopFields & AiFields & { prompt_id: string }
  users: string[]
}

export interface ActionPopForm extends BaseMeta {
  pop_type: PopType.ActionPop
  data: BaseData & ActionPopFields
  users: string[]
  tags: string[]
  groups: string[]
}

export interface LinkPopForm extends BaseMeta {
  pop_type: PopType.LinkPop
  data: BaseData & {
    pop_title: string
    pop_body: string
    content?: string
  }
}

export type PopFormValues =
  | (
      | DefaultPopForm
      | RePopForm
      | ChatGptPopForm
      | FakeRePopForm
      | AiPopForm
      | AiRePopForm
      | ActionPopForm
      | LinkPopForm
    ) & {
      pop_id?: string
    }

export enum PopType {
  Default = 1,
  RePop = 2,
  ChatGpt = 3,
  FakeRe = 4,
  AiPop = 5,
  AiRePop = 6,
  ActionPop = 7,
  LinkPop = 8,
}

export interface ServerPopData {
  pk?: string
  pop_type: number
  is_long_range_pop?: boolean
  is_send_push_notification?: boolean
  users?: string[]
  groups?: string[]
  tags?: string[]
  is_scheduled?: boolean
  pop_title?: string
  pop_body?: string
  content?: string
  ack_btn_text?: string
  push_title?: string
  push_body?: string
  repop_push_title?: string
  repop_push_body?: string
  profile_image_url?: string | null
  pop_image_url?: string | null
  video?: string | null
  notification?: {
    title?: string
    body?: string
    data?: {
      body?: string
      title?: string
    }
  }
  // RePop fields
  is_repop_send_push_notification?: boolean
  repop_main_btn?: string
  repop_sec_btn?: string
  is_pop_post?: boolean
  repop_users?: string[]
  repop_tags?: string[]
  repop_groups?: string[]
  // AI fields
  prompt_id?: string
  is_inj_reports?: boolean
  is_voice_mode?: boolean
  // ActionPop fields
  reference_type?: number
  reference_params?: Record<string, any>
  [key: string]: any
}

export function transformServerDataToPopFormValues(serverData: ServerPopData): PopFormValues {
  const baseFields = {
    rid: serverData.rid || '',
    is_long_range_pop: serverData.is_long_range_pop || false,
    pop_type: serverData.pop_type,
    is_send_push_notification: serverData.is_send_push_notification ?? !isEmpty(serverData.notification),
    users: serverData.users || [],
    groups: serverData.groups || [],
    tags: serverData.tags || [],
    is_scheduled: serverData.is_scheduled,
    pop_id: serverData.pk,
  }

  const baseData = {
    pop_title: serverData.pop_title,
    pop_body: serverData.pop_body,
    ack_btn_text: serverData.ack_btn_text,
    push_title: serverData.push_title ?? serverData.notification?.title,
    push_body: serverData.push_body ?? serverData.notification?.body,
    repop_push_title: serverData.repop_push_title,
    repop_push_body: serverData.repop_push_body,
    profile_image_url: serverData.profile_image_url,
    pop_image_url: serverData.pop_image_url,
    video: serverData.video,
  }

  switch (serverData.pop_type) {
    case PopType.LinkPop: {
      return {
        ...baseFields,
        is_long_range_pop: true,
        pop_type: PopType.LinkPop,
        data: {
          ...baseData,
          pop_title: serverData.pop_title || '',
          pop_body: serverData.pop_body || '',
          content: serverData.content || '',
        },
      }
    }

    case PopType.Default:
      return {
        ...baseFields,
        pop_type: PopType.Default,
        data: baseData,
        users: serverData.users || [],
        tags: serverData.tags || [],
        groups: serverData.groups || [],
      } as DefaultPopForm & { pop_id?: string }

    case PopType.RePop:
      return {
        ...baseFields,
        pop_type: PopType.RePop,
        data: {
          ...baseData,
          is_repop_send_push_notification:
            serverData.is_repop_send_push_notification ?? (serverData.repop_push_title && serverData.repop_push_body),
          repop_main_btn: serverData.repop_main_btn,
          repop_sec_btn: serverData.repop_sec_btn,
          is_pop_post: serverData.is_pop_post,
          repop_users: serverData.repop_users,
          repop_tags: serverData.repop_tags,
          repop_groups: serverData.repop_groups,
        },
        is_custom_avatar: false, // Default value, adjust as needed
      } as RePopForm & { pop_id?: string }

    case PopType.ChatGpt:
      return {
        ...baseFields,
        pop_type: PopType.ChatGpt,
        data: {
          pop_body: serverData.pop_body,
          ack_btn_text: serverData.ack_btn_text,
          push_title: serverData.push_title,
          push_body: serverData.push_body,
        },
        users: serverData.users || [],
      } as ChatGptPopForm & { pop_id?: string }

    case PopType.FakeRe:
      return {
        ...baseFields,
        pop_type: PopType.FakeRe,
        data: {
          ...baseData,
          is_repop_send_push_notification:
            serverData.is_repop_send_push_notification ?? (serverData.repop_push_title && serverData.repop_push_body),
          repop_main_btn: serverData.repop_main_btn,
          repop_sec_btn: serverData.repop_sec_btn,
          is_pop_post: serverData.is_pop_post,
          repop_users: serverData.repop_users,
          repop_tags: serverData.repop_tags,
          repop_groups: serverData.repop_groups,
        },
        users: serverData.users || [],
      } as FakeRePopForm & { pop_id?: string }

    case PopType.AiPop:
      return {
        ...baseFields,
        pop_type: PopType.AiPop,
        data: {
          ...baseData,
          prompt_id: serverData.prompt_id || '',
          is_inj_reports: serverData.is_inj_reports,
          is_voice_mode: serverData.is_voice_mode,
        },
      } as AiPopForm & { pop_id?: string }

    case PopType.AiRePop:
      return {
        ...baseFields,
        pop_type: PopType.AiRePop,
        data: {
          ...baseData,
          prompt_id: serverData.prompt_id || '',
          is_inj_reports: serverData.is_inj_reports,
          is_voice_mode: serverData.is_voice_mode,
          is_repop_send_push_notification:
            serverData.is_repop_send_push_notification ?? (serverData.repop_push_title && serverData.repop_push_body),
          repop_main_btn: serverData.repop_main_btn,
          repop_sec_btn: serverData.repop_sec_btn,
          is_pop_post: serverData.is_pop_post,
          repop_users: serverData.repop_users,
          repop_tags: serverData.repop_tags,
          repop_groups: serverData.repop_groups,
        },
        users: serverData.users || [],
      } as AiRePopForm & { pop_id?: string }

    case PopType.ActionPop:
      return {
        ...baseFields,
        pop_type: PopType.ActionPop,
        data: {
          ...baseData,
          reference_type: serverData.reference_type ?? POP_REFERENCE_TYPE.PRODUCT_PERFORMANCE,
          reference_params: serverData.reference_params,
        },
        users: serverData.users || [],
        tags: serverData.tags || [],
        groups: serverData.groups || [],
      } as ActionPopForm & { pop_id?: string }

    default:
      // Fallback to Default if unknown type
      return {
        ...baseFields,
        pop_type: PopType.Default,
        data: baseData,
        users: serverData.users || [],
        tags: serverData.tags || [],
        groups: serverData.groups || [],
      } as DefaultPopForm & { pop_id?: string }
  }
}
